import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Button as DSButton } from "@/design-system/components";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[14px] text-[16px] font-medium tracking-[-0.01em] transition-all duration-base ease-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 [&_svg]:stroke-[1.75] outline-none focus-visible:ring-[2px] focus-visible:ring-[#22D3EE] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1020]",
  {
    variants: {
      variant: {
        default:
          "border border-transparent bg-[linear-gradient(90deg,#22D3EE_0%,#8B5CF6_50%,#D946EF_100%)] text-white hover:brightness-110 hover:shadow-[0_0_20px_rgba(139,92,246,0.35)]",
        destructive:
          "border border-transparent bg-[linear-gradient(90deg,#22D3EE_0%,#8B5CF6_50%,#D946EF_100%)] text-white hover:brightness-110 hover:shadow-[0_0_20px_rgba(139,92,246,0.35)]",
        outline:
          "border border-[rgba(255,255,255,0.05)] bg-transparent text-white hover:bg-white/[0.04]",
        secondary:
          "border border-[rgba(255,255,255,0.05)] bg-transparent text-white hover:bg-white/[0.04]",
        ghost:
          "border border-transparent text-white shadow-none hover:bg-white/[0.04]",
        link: "text-[#22D3EE] underline-offset-4 hover:underline shadow-none",
      },
      size: {
        default: "h-11 px-4 py-2 has-[>svg]:px-3",
        sm: "h-10 gap-1.5 px-3 has-[>svg]:px-2.5 text-[14px]",
        lg: "h-12 px-6 has-[>svg]:px-4",
        icon: "size-11 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  if (asChild) {
    const Comp = Slot;
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }

  const dsVariant = variant === "default" || variant === "destructive" ? "primary" : variant === "outline" || variant === "secondary" ? "secondary" : "ghost";
  const sizeClass = size === "sm" ? "h-10 px-3 text-[14px]" : size === "lg" ? "h-12 px-6" : size === "icon" ? "h-11 w-11 p-0" : "h-11 px-4";

  return (
    <DSButton
      variant={dsVariant}
      data-slot="button"
      className={cn(sizeClass, className)}
      ref={ref}
      {...props}
    />
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };
